
import { useState } from 'react';
import { ChevronDown, RotateCcw } from 'lucide-react';

import { Button, Card, CardBody, CardHead, Field, Input, Select, Toggle } from '../../components/ui';
import type { DensityForm } from './density';
import { DENSITY_PROFILES, profileLabel } from './format';
import type { AppliedDensity } from './types';
import styles from './placement.module.css';

type TuningKey =
  | 'insertsPerMinute'
  | 'insertsPer100Words'
  | 'minInsertMs'
  | 'maxInsertMs'
  | 'minGapMs'
  | 'hookGuardMs'
  | 'tailGuardMs';

const TUNING_KEYS: readonly TuningKey[] = [
  'insertsPerMinute',
  'insertsPer100Words',
  'minInsertMs',
  'maxInsertMs',
  'minGapMs',
  'hookGuardMs',
  'tailGuardMs',
];

const TUNING_ID = 'placement-tuning';

interface Props {
  value: DensityForm;
  onChange: (value: DensityForm) => void;
  disabled: boolean;
  applied: AppliedDensity | null;
  onUseApplied: () => void;
  index?: string;
}

export function DensityStep({
  value,
  onChange,
  disabled,
  applied,
  onUseApplied,
  index = '02',
}: Props) {
  const [tuning, setTuning] = useState(false);
  const set = <K extends keyof DensityForm>(key: K, next: DensityForm[K]) =>
    onChange({ ...value, [key]: next });

  const touched = TUNING_KEYS.filter((key) => value[key].trim() !== '').length;

  return (
    <Card>
      <CardHead index={index} title="Настройки" hint="пустое поле = пресет" />

      <CardBody pad className={styles.panelBody}>
        <Field layout="stacked" label="Длина вставок">
          {(id) => (
            <Select
              id={id}
              value={value.profile}
              onChange={(event) => set('profile', event.target.value)}
              disabled={disabled}
            >
              {DENSITY_PROFILES.map((profile) => (
                <option key={profile.value} value={profile.value}>
                  {profile.label} · {profile.hint}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <div className={styles.divider} />

        <NumberField
          id="placement-alts"
          label="Показывать запасные варианты"
          value={value.alternatives}
          onChange={(next) => set('alternatives', next)}
          disabled={disabled}
          step="1"
          min="0"
          max="10"
        />
        <span className={styles.hint}>0 = не искать</span>

        <div className={styles.divider} />

        <Button
          size="sm"
          className={styles.tuningToggle}
          aria-expanded={tuning}
          aria-controls={TUNING_ID}
          onClick={() => setTuning((open) => !open)}
        >
          <ChevronDown
            size={12}
            strokeWidth={1.8}
            className={`${styles.chevron} ${tuning ? styles.chevronOpen : ''}`}
            aria-hidden="true"
          />
          Тонкая настройка
          <span className={styles.tuningBadge}>
            {touched > 0 ? `задано ${touched}` : 'всё по пресету'}
          </span>
        </Button>

        {tuning && (
          <div className={styles.tuning} id={TUNING_ID}>
            <p className={styles.groupTitle}>сколько вставок</p>
            <div className={styles.fieldRow}>
              <NumberField
                id="placement-ipm"
                label="Не больше, на минуту"
                value={value.insertsPerMinute}
                onChange={(next) => set('insertsPerMinute', next)}
                disabled={disabled}
                step="0.5"
                min="0"
              />
              <NumberField
                id="placement-ipw"
                label="Не больше, на 100 слов"
                value={value.insertsPer100Words}
                onChange={(next) => set('insertsPer100Words', next)}
                disabled={disabled}
                step="0.5"
                min="0"
              />
            </div>

            <div className={styles.divider} />

            <p className={styles.groupTitle}>длительность и промежутки</p>
            <div className={styles.fieldRow}>
              <NumberField
                id="placement-min-insert"
                label="Мин. вставка, мс"
                value={value.minInsertMs}
                onChange={(next) => set('minInsertMs', next)}
                disabled={disabled}
                step="100"
                min="1"
              />
              <NumberField
                id="placement-max-insert"
                label="Макс. вставка, мс"
                value={value.maxInsertMs}
                onChange={(next) => set('maxInsertMs', next)}
                disabled={disabled}
                step="100"
                min="1"
              />
              <NumberField
                id="placement-gap"
                label="Мин. промежуток, мс"
                value={value.minGapMs}
                onChange={(next) => set('minGapMs', next)}
                disabled={disabled}
                step="100"
                min="1"
              />
              <NumberField
                id="placement-hook"
                label="Защита начала, мс"
                value={value.hookGuardMs}
                onChange={(next) => set('hookGuardMs', next)}
                disabled={disabled}
                step="100"
                min="0"
              />
              <NumberField
                id="placement-tail"
                label="Защита конца, мс"
                value={value.tailGuardMs}
                onChange={(next) => set('tailGuardMs', next)}
                disabled={disabled}
                step="100"
                min="0"
              />
            </div>

            <div className={styles.checkRow}>
              <Toggle
                checked={value.allowAdjacent}
                onChange={(next) => set('allowAdjacent', next)}
                disabled={disabled}
                label="Разрешать вставки вплотную"
              />
            </div>

            <div className={styles.divider} />

            <p className={styles.groupTitle}>подбор медиа</p>
            <div className={styles.checkRow}>
              <Toggle
                checked={value.allowMediaReuse}
                onChange={(next) => set('allowMediaReuse', next)}
                disabled={disabled}
                label="Разрешать один ролик на несколько вставок"
              />
              <span className={styles.hint}>Спасает на маленькой библиотеке, но повторы видно.</span>
            </div>
          </div>
        )}
      </CardBody>

      {applied && (
        <div className={styles.panelFoot}>
          <span className={styles.footHint}>
            Прошлый подбор: пресет «{profileLabel(applied.profile)}».
          </span>
          <Button size="sm" onClick={onUseApplied} disabled={disabled}>
            <RotateCcw size={13} strokeWidth={1.6} /> Взять его пороги
          </Button>
        </div>
      )}
    </Card>
  );
}

interface NumberFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  step?: string;
  min?: string;
  max?: string;
}

function NumberField({ id, label, value, onChange, disabled, step, min, max }: NumberFieldProps) {
  return (
    <Field layout="stacked" label={label} htmlFor={id}>
      <Input
        id={id}
        size="sm"
        mono
        className={styles.numberInput}
        type="number"
        inputMode="decimal"
        value={value}
        step={step}
        min={min}
        max={max}
        placeholder="пресет"
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}
